import { apiFetch } from '../lib/apiFetch';

import {
  PrivateEventNotFoundError,
  PrivateEventValidationError,
  type PrivateEventDetail,
} from './privateEvents';

/** Exactly one location shape, as create sends it. */
export type PrivateEventLocationInput =
  | { venueId: string }
  | {
      regionId: string;
      locationName: string;
      addressLine1: string;
      addressLine2?: string | null;
      city: string;
      state: string;
      zipCode: string;
      timeZone: string;
    }
  | { locationTbd: true; regionId: string; timeZone: string };

/** Every field optional: a section sends only what it changed. */
export interface UpdatePrivateEventInput {
  workshopTypeId?: string;
  startsAt?: string;
  endsAt?: string;
  clientName?: string;
  headcount?: number;
  dealValueCents?: number | null;
  hubspotDealId?: string | null;
  depositReceived?: boolean;
  hubspotPaymentId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  addOns?: string[];
  notes?: string | null;
  ownerUserId?: string;
  nationalCoordinatorUserId?: string | null;
  dealOriginatorUserId?: string | null;
  location?: PrivateEventLocationInput;
}

/** A 403. With field errors it is a location outside the caller's regions;
 *  without, the caller can no longer edit this event at all. */
export class PrivateEventEditForbiddenError extends Error {
  constructor(
    message: string,
    public readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'PrivateEventEditForbiddenError';
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  fieldErrors?: Record<string, string>;
}

export async function updatePrivateEvent(
  id: string,
  patch: UpdatePrivateEventInput,
): Promise<PrivateEventDetail> {
  const res = await apiFetch(`/api/v1/private-events/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (res.ok) {
    const data = (await res.json()) as { privateEvent: PrivateEventDetail };
    return data.privateEvent;
  }
  const err = (await res.json().catch(() => ({}))) as ErrorBody;
  if (res.status === 400 && err.fieldErrors) {
    throw new PrivateEventValidationError(err.fieldErrors, err.message ?? 'Validation failed');
  }
  if (res.status === 403) {
    throw new PrivateEventEditForbiddenError(
      err.message ?? 'You don’t have permission to edit this private event.',
      err.fieldErrors,
    );
  }
  if (res.status === 404) throw new PrivateEventNotFoundError();
  throw new Error(err.error || err.message || `Failed to update private event: ${res.status}`);
}
